import { useEffect, useRef, useState } from 'react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../../../providers/ToastProvider';
import { formatCreatedDate } from '../../../../utils/dates';
import { copyToClipboard } from '../../../../utils/clipboard';

const MAX_TITLE_LENGTH = 200;

interface InlineTitleProps {
    title: string;
    disabled: boolean;
    onSave: (title: string) => void;
}

// No pencil icon - the title itself is the click target, with only a faint
// hover background and a text cursor as the affordance.
function InlineTitle({ title, disabled, onSave }: InlineTitleProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(title);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!isEditing) setDraft(title);
    }, [title, isEditing]);

    useEffect(() => {
        if (isEditing) inputRef.current?.focus();
    }, [isEditing]);

    const commit = () => {
        setIsEditing(false);
        const trimmed = draft.trim();
        if (trimmed && trimmed !== title) onSave(trimmed);
        else setDraft(title);
    };

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                type="text"
                value={draft}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') commit();
                    if (e.key === 'Escape') {
                        setDraft(title);
                        setIsEditing(false);
                    }
                }}
                className="w-full h-9 -mx-1.5 px-1.5 border-0 bg-transparent text-[22px] font-bold text-gray-900 focus:outline-none"
            />
        );
    }

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={() => setIsEditing(true)}
            style={{ cursor: disabled ? 'default' : 'text' }}
            className="w-full text-left rounded-lg -mx-1.5 px-1.5 py-0.5 hover:bg-gray-50 transition-colors disabled:hover:bg-transparent"
        >
            <h1 className="text-[22px] font-bold text-gray-900 break-words">{title}</h1>
        </button>
    );
}

interface ReferenceFieldProps {
    referenceNo: string;
}

// A labelled, read-only "field" treatment (rather than ReferencePill's large
// prominent number) - this card isn't the thing read aloud to a caller
// anymore, just a compact reference lookup with a copy affordance attached.
function ReferenceField({ referenceNo }: ReferenceFieldProps) {
    const { showToast } = useToast();

    const handleCopy = async () => {
        const copied = await copyToClipboard(referenceNo);
        showToast(
            copied ? 'Reference copied' : 'Could not copy reference',
            copied ? 'success' : 'error'
        );
    };

    return (
        <div className="flex flex-col gap-1 w-[200px]">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em]">
                Reference
            </span>
            <div className="flex items-center gap-1 h-9 pl-3 pr-1 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="flex-1 min-w-0 font-mono text-[13px] text-gray-600 truncate">
                    {referenceNo}
                </span>
                <button
                    type="button"
                    onClick={handleCopy}
                    title="Copy reference"
                    className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-800 hover:bg-white transition-colors shrink-0"
                >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

interface CommunicationIdentityCardProps {
    title: string;
    referenceNo: string;
    createdAt: string;
    isSaving: boolean;
    onTitleSave: (title: string) => void;
}

// Headline card: inline-editable title + creation date, reference on the
// right. Delete-thread lives in the meta rail instead.
export function CommunicationIdentityCard({
    title,
    referenceNo,
    createdAt,
    isSaving,
    onTitleSave
}: CommunicationIdentityCardProps) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.04)] px-5 py-4 flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1 flex flex-col gap-1">
                <InlineTitle title={title} disabled={isSaving} onSave={onTitleSave} />
                <span className="text-[12px] text-gray-400 px-1.5">
                    Created {formatCreatedDate(createdAt)}
                </span>
            </div>
            <ReferenceField referenceNo={referenceNo} />
        </div>
    );
}
